import { create, insertMultiple, search as oramaSearch, type Orama } from '@orama/orama'
import { createTokenizer as createMandarinTokenizer } from '@orama/tokenizers/mandarin'
import { createTokenizer as createJapaneseTokenizer } from '@orama/tokenizers/japanese'
import { stopwords as mandarinStopwords } from '@orama/stopwords/mandarin'
import { stopwords as japaneseStopwords } from '@orama/stopwords/japanese'
import { locale as cookieLocale } from '../../../shared/locales'

export interface SearchDocument {
  id: string
  pageId: string
  label: string
  content: string
  breadcrumbs: string[]
  url: string
  category: string
  keywords: string[]
}

export interface MenuItem {
  id: string
  label: string
  labelEn: string
  url: string
  target?: string
  breadcrumbs: string[]
  keywords: string[]
  contents: string[]
}

export interface SearchConfig {
  limit?: number
  threshold?: number
  tolerance?: number
  boost?: Record<string, number>
}

export interface SearchResultHit {
  id: string
  score: number
  document: SearchDocument
}

export interface SearchResult {
  hits: SearchResultHit[]
  count: number
  elapsed?: {
    formatted: string
    raw: number
  }
}

const dbSchema = {
  id: 'string',
  pageId: 'string',
  label: 'string',
  content: 'string',
  breadcrumbs: 'string[]',
  url: 'string',
  category: 'string',
  keywords: 'string[]'
} as const

const ORAMA_LANGUAGE_BY_LOCALE: Record<string, string> = {
  'en-US': 'english',
  'es-ES': 'spanish',
  'de-DE': 'german',
  'fr-FR': 'french',
  'pt-PT': 'portuguese',
  'ru-RU': 'russian',
  'it-IT': 'italian',
  'nl-NL': 'dutch',
  'sv-SE': 'swedish',
  'id-ID': 'indonesian',
  'tr-TR': 'turkish'
}

export function toOramaLanguage(locale: string): string {
  return ORAMA_LANGUAGE_BY_LOCALE[locale] ?? 'english'
}

type DatabaseSchema = typeof dbSchema

function createDatabase(locale: string = cookieLocale ?? 'en-US'): Orama<DatabaseSchema> {
  if (locale === 'zh-CN' || locale === 'zh-TW') {
    return create({
      schema: dbSchema,
      components: {
        tokenizer: createMandarinTokenizer({
          language: 'mandarin',
          stopWords: mandarinStopwords
        })
      }
    })
  }

  if (locale === 'jp-JP') {
    return create({
      schema: dbSchema,
      components: {
        tokenizer: createJapaneseTokenizer({
          language: 'japanese',
          stopWords: japaneseStopwords
        })
      }
    })
  }

  return create({
    schema: dbSchema,
    language: toOramaLanguage(locale)
  })
}

let db: Orama<DatabaseSchema> = createDatabase()

function resetDatabase(locale: string = cookieLocale ?? 'en-US') {
  db = createDatabase(locale)
}

async function indexDocuments(documents: SearchDocument[]) {
  await insertMultiple(db, documents)
  return db
}

export function convertMenuToSearchDoc(menuItem: MenuItem): SearchDocument {
  // Build keywords: use provided keywords or fallback to both label variants
  let keywords = menuItem.keywords || []
  if (!Array.isArray(keywords)) {
    keywords = []
  }
  keywords = keywords.filter((k) => typeof k === 'string' && k.trim() !== '')
  if (keywords.length === 0) {
    keywords = [menuItem.label, menuItem.labelEn].filter((k): k is string => Boolean(k))
  }

  // Build content: combine all text fields for comprehensive search
  const content = [menuItem.label, menuItem.labelEn, ...(menuItem.contents || [])]
    .filter((text): text is string => Boolean(text))
    .join(' ')

  return {
    id: menuItem.id,
    pageId: menuItem.id,
    label: menuItem.label || menuItem.labelEn || '',
    content,
    breadcrumbs: Array.isArray(menuItem.breadcrumbs) ? menuItem.breadcrumbs : [],
    url: menuItem.url || '',
    category: (menuItem.breadcrumbs?.[0] || 'Navigation') as string,
    keywords
  }
}

export function convertMenusToSearchDocs(menuItems: MenuItem[]): SearchDocument[] {
  // Dedupe by URL (not id) to handle cases where same page appears in multiple menu hierarchies
  // This ensures each unique page URL is indexed only once with its first occurrence's breadcrumb
  const seen = new Set<string>()
  const docs: SearchDocument[] = []

  for (const item of menuItems) {
    // Use URL as dedupe key since it represents the actual unique page
    // while id might be reused across different menu contexts
    const dedupeKey = item.url || item.id

    if (seen.has(dedupeKey)) {
      console.warn(`Duplicate menu item URL: ${dedupeKey}, skipping...`)
      continue
    }
    seen.add(dedupeKey)

    try {
      docs.push(convertMenuToSearchDoc(item))
    } catch (error) {
      console.error(`Failed to convert menu item ${item.id}:`, error)
    }
  }

  return docs
}

export class SearchService {
  private initialized = false
  private documentCount = 0

  /**
   * Check if a search term consists only of stopwords
   * This helps avoid returning irrelevant results when users search common words like "是的", "的", etc.
   */
  private isOnlyStopwords(term: string): boolean {
    // Simple heuristic: check if the term is in the common Chinese stopwords
    // In a more robust implementation, we'd tokenize and check each token
    const commonStopwords = mandarinStopwords

    // Check if the entire term is a stopword
    if (commonStopwords.includes(term)) {
      return true
    }

    // For multi-character terms, check if all individual characters are stopwords
    // This is a simplified check - ideally we'd use the actual tokenizer
    const chars = term.split('')
    if (chars.length > 0 && chars.every((char) => commonStopwords.includes(char))) {
      return true
    }

    return false
  }

  async init(documents: SearchDocument[], locale: string = cookieLocale ?? 'en-US'): Promise<void> {
    resetDatabase(locale)
    await indexDocuments(documents)
    this.documentCount = documents.length
    this.initialized = true

    // console.log(`✅ Search service initialized with ${this.documentCount} documents`)
  }

  isReady(): boolean {
    return this.initialized
  }

  getDocumentCount(): number {
    return this.documentCount
  }

  async search(term: string, config?: SearchConfig): Promise<SearchResult> {
    if (!this.initialized) {
      throw new Error('Search service not initialized. Call init() first.')
    }

    // Handle empty search term
    const normalizedTerm = term.trim()
    if (!normalizedTerm) {
      return {
        hits: [],
        count: 0
      }
    }

    // Check if the search term consists only of stopwords for Chinese locales
    const locale = cookieLocale ?? 'en-US'
    if ((locale === 'zh-CN' || locale === 'zh-TW') && this.isOnlyStopwords(normalizedTerm)) {
      console.warn(`Search term "${normalizedTerm}" contains only stopwords, no meaningful results`)
      return {
        hits: [],
        count: 0
      }
    }

    const {
      limit = 20,
      threshold = 0.4,
      tolerance = locale === 'zh-CN' || locale === 'zh-TW' ? 0 : 1,
      boost = {
        label: 3,
        keywords: 2,
        content: 1
      }
    } = config || {}

    const results = await oramaSearch(db, {
      term: normalizedTerm,
      limit,
      threshold,
      tolerance,
      boost,
      properties: ['label', 'content', 'keywords']
    })

    return {
      hits: results.hits as SearchResultHit[],
      count: results.count,
      elapsed: results.elapsed
    }
  }
}

export const searchService = new SearchService()
